/**
 * Stable UUIDs for structural FHIR resources (AccessPolicies).
 *
 * These IDs are referenced by medplum-ui at runtime (e.g. POLICY_IDS in
 * src/features/rbac/constants.ts). They must not change between environments.
 *
 * Test fixture IDs (patients, appointments, etc.) live in the seed script.
 */

export const POLICY_IDS = {
  provider: '11111111-1111-4111-8111-111111111101',
  supervisor: '11111111-1111-4111-8111-111111111102',
  patientSelf: '11111111-1111-4111-8111-111111111103',
  unifyAdmin: '11111111-1111-4111-8111-111111111107',
  practiceAdmin: '11111111-1111-4111-8111-111111111110',
  practiceOwner: '11111111-1111-4111-8111-111111111115',
} as const;

/** Logical key for a canonical AccessPolicy (matches POLICY_IDS keys). */
export type PolicyKey = keyof typeof POLICY_IDS;

/**
 * Stable `meta.tag` system for canonical AccessPolicies.
 *
 * Managed Medplum (api.medplum.com) rejects client-assigned resource ids unless
 * the project enables `update-as-create` (super-admin only). So we no longer
 * rely on the POLICY_IDS UUIDs existing on the server. Instead every canonical
 * AccessPolicy carries a `meta.tag` of { system: POLICY_TAG_SYSTEM, code:
 * <POLICY_TAG_CODES[key]> }, and callers resolve the server-assigned id at
 * runtime by searching `_tag`. (AccessPolicy has no `identifier` element, so a
 * tag is the only portable stable handle.) POLICY_IDS remains the
 * local/super-admin fallback where the fixed UUIDs exist.
 */
export const POLICY_TAG_SYSTEM = 'https://unify.health/fhir/access-policy';

/** Stable tag code per canonical AccessPolicy. */
export const POLICY_TAG_CODES: Record<PolicyKey, string> = {
  provider: 'provider',
  supervisor: 'supervisor',
  patientSelf: 'patient-self',
  unifyAdmin: 'unify-admin',
  practiceAdmin: 'practice-admin',
  practiceOwner: 'practice-owner',
} as const;

/** FHIR `meta.tag` coding for a canonical AccessPolicy, used for runtime id resolution. */
export function accessPolicyTag(key: PolicyKey): { system: string; code: string } {
  return { system: POLICY_TAG_SYSTEM, code: POLICY_TAG_CODES[key] };
}

/** Reverse lookup: canonical tag code → PolicyKey (used to migrate legacy fixed-UUID policies). */
export function policyKeyForTagCode(code: string): PolicyKey | undefined {
  return (Object.keys(POLICY_TAG_CODES) as PolicyKey[]).find((k) => POLICY_TAG_CODES[k] === code);
}

/** Bot AccessPolicies are tagged separately from human role policies. */
export const BOT_POLICY_TAG_SYSTEM = 'https://unify.health/fhir/bot-access-policy';

export const BOT_POLICY_TAG_CODES = {
  consent: 'consent',
  formSubmission: 'form-submission',
  supervisionNotesDue: 'supervision-notes-due',
  aiTransferHistorySummary: 'ai-transfer-history-summary',
  aiTreatmentPlanGenerator: 'ai-treatment-plan-generator',
  aiTreatmentPlanRevision: 'ai-treatment-plan-revision',
  appointmentEncounterCreation: 'appointment-encounter-creation',
  // ENG-3634 platform bots: one tagged policy per code.
  rulesEngine: 'rules-engine',
  scheduledActionExecutor: 'scheduled-action-executor',
  taskTimer: 'task-timer',
  criticalCleanup: 'critical-cleanup',
  assessmentScoring: 'assessment-scoring',
  documentAttachmentDownload: 'document-attachment-download',
  chimeTelehealth: 'chime-telehealth',
  chimeGuestJoin: 'chime-guest-join',
  chimeEvents: 'chime-events',
  sessionTranscriptPipeline: 'session-transcript-pipeline',
  // ENG-3764 D29: the one event-driven Task writer.
  taskEvents: 'task-events',
  feedbackAndBugReports: 'feedback-and-bug-reports',
} as const;

/**
 * Stable UUIDs for CodeSystem resources.
 * Seeded by cloud/seed-config or local/seed.
 */
export const CODE_SYSTEM_IDS = {
  // f0–f2 — kept out of the pilot Practitioner id block (…2201–2209) so local:seed
  // can PUT CodeSystems and Practitioners in one transaction without fullUrl collisions.
  profileRole: '22222222-2222-4222-8222-2222222222f0',
  practitionerRole: '22222222-2222-4222-8222-2222222222f1',
  therapyServiceType: '22222222-2222-4222-8222-2222222222f2',
} as const;
